<script lang="ts" setup>

import {useUserStore} from '@/ts/store/user-store';
import {storeToRefs} from 'pinia';
import {computed, ref, onMounted, watch} from 'vue';
import dayjs from 'dayjs';
import FormattedDate from '@/vue/atoms/formatted-date.vue';
import {ContractStatus, type Contract, type PurchaseLicense, type PaymentMethodInfo, type ProductInfo} from '@/ts/types/dto/user-dto';
import {planDisplay} from '@/ts/utils/plan-display';
import BadgePill from '@/vue/atoms/badge-pill.vue';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import {useApi} from '@/ts/composables/stateful/use-api';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import {LicenseProvisionTypeDTO} from '@/ts/types/dto/sign-in-dto';
import {useLogger} from '@/ts/composables/pure/use-logger';
import {useRoute, useRouter} from 'vue-router';
import {useQuasar} from 'quasar';
import {useI18n} from 'vue-i18n';
import {ErrorCodesDto} from '@/ts/types/dto/error-codes-dto';
import {useLockNotice} from '@/ts/composables/stateful/use-contract-expiry-guard';
import {useProfileDateFormat} from '@/ts/composables/stateful/use-profile-date-format';
import {formatCurrency} from '@/ts/utils/number-format';
import PaymentDialog from '@/vue/organisms/payment-dialog.vue';
import ContentLockedDialog from '@/vue/organisms/content-locked-dialog.vue';
import UpdateCardDialog from '@/vue/organisms/update-card-dialog.vue';
import RenewVoucherDialog from '@/vue/organisms/renew-voucher-dialog.vue';
import {sleep} from '@/ts/utils/pure-functions';

const userStore = useUserStore();
const api = useApi();
const logger = useLogger('account-subscription');
const route = useRoute();
const router = useRouter();
const $q = useQuasar();
const {t: $t} = useI18n();
const datePatterns = useProfileDateFormat();

const {userData} = storeToRefs(userStore);

// License contracts history logic
const licenseContracts = ref<Contract[]>([]);
const loadingContracts = ref(false);

const currentContract = computed((): Contract | null => {
  return userData.value?.lastContract ?? null;
});

const hasContract = computed((): boolean => {
  return Boolean(currentContract.value);
});

const license = computed(() => {
  return currentContract.value?.license ?? null;
});

const provisionType = computed((): LicenseProvisionTypeDTO | null => {
  return license.value?.provisionType ?? null;
});

const validityPeriodInDays = computed((): number | null => {
  return license.value?.validityPeriodInDays ?? null;
});

const isVoucherLicense = computed((): boolean => {
  return provisionType.value === LicenseProvisionTypeDTO.VOUCHER_BASED;
});

const isDirectPaymentLicense = computed((): boolean => {
  return provisionType.value === LicenseProvisionTypeDTO.DIRECT_PAYMENT;
});

const isGiftedLicense = computed((): boolean => {
  return provisionType.value === LicenseProvisionTypeDTO.GIFTED;
});

const paymentSubscription = computed(() => {
  return currentContract.value?.paymentSubscription ?? null;
});

const isSubscriptionCanceled = computed((): boolean => {
  return paymentSubscription.value?.canceled === true;
});

const isLifetimeValidity = computed((): boolean => {
  // For voucher/gift: null validityPeriodInDays means lifetime
  // For direct_payment: ignore validityPeriodInDays, not applicable
  if (provisionType.value === LicenseProvisionTypeDTO.DIRECT_PAYMENT) {
    return false; // Direct payment is managed by subscription periods
  }
  return validityPeriodInDays.value === null;
});

const LONG_TERM_GIFTED_THRESHOLD_DAYS = 30;

const isLongTermGifted = computed((): boolean => {
  return isGiftedLicense.value
    && !isLifetimeValidity.value
    && validityPeriodInDays.value !== null
    && validityPeriodInDays.value > LONG_TERM_GIFTED_THRESHOLD_DAYS;
});

const contractStatus = computed((): ContractStatus | null => {
  return currentContract.value?.status ?? null;
});

const expireDate = computed((): dayjs.Dayjs | null => {
  if (!currentContract.value || !license.value) {
    return null;
  }

  // Rule: Use contract.status for state, but calculate expiry date to show it
  if (provisionType.value === LicenseProvisionTypeDTO.DIRECT_PAYMENT) {
    // For paid: expiry = paymentSubscription.currentPeriodEnd
    if (paymentSubscription.value?.currentPeriodEnd) {
      return dayjs(paymentSubscription.value.currentPeriodEnd);
    }
    return null;
  }
    // For voucher/gift: expiry = contract.createDate + license.validityPeriodInDays
    if (validityPeriodInDays.value && !isLifetimeValidity.value) {
      return dayjs(currentContract.value.createDate).add(validityPeriodInDays.value, 'days');
    }
    return null; // Lifetime validity
});

const hasExpired = computed((): boolean => {
  // Always use contract.status for state determination
  return contractStatus.value === ContractStatus.EXPIRED;
});

const daysLeftToExpireDate = computed((): number => {
  if (expireDate.value && !hasExpired.value) {
    return dayjs(expireDate.value).startOf('day').diff(dayjs().startOf('day'), 'days');
  }
  return 0;
});


// Purchasable licenses logic
const purchasableLicenses = ref<PurchaseLicense[]>([]);
const loadingLicenses = ref(false);

// Payment dialog state
const showPaymentDialog = ref(false);
const selectedLicenseId = ref<string | null>(null);

// Subscription action loading state
const subscriptionActionInProgress = ref(false);

// Payment method state
const paymentMethod = ref<PaymentMethodInfo | null>(null);
const loadingPaymentMethod = ref(false);
const showUpdateCardDialog = ref(false);

// Renew voucher dialog state
const showRenewVoucherDialog = ref(false);

const openPaymentDialog = (licenseId: string): void => {
  selectedLicenseId.value = licenseId;
  showPaymentDialog.value = true;
};

const refreshUserData = async(): Promise<void> => {
  const updatedUser = await api.me.getMe();
  userStore.setUserData(updatedUser);
};

const handlePaymentPending = (): void => {
  showPaymentDialog.value = false;
  $q.notify({type: 'warning', message: $t('payment_pending_banner'), timeout: 10000});
};

const handlePaymentError = (error: string): void => {
  logger.error('Payment error:', error)();
};

const shouldShowPaymentMethod = computed(() => {
  return isDirectPaymentLicense.value && !hasExpired.value;
});

const PAYMENT_METHOD_POLLING_MAX_ATTEMPTS = 5;
const PAYMENT_METHOD_POLLING_INTERVAL_MS = 3000;

const loadPaymentMethod = async(poll = false): Promise<void> => {
  if (!shouldShowPaymentMethod.value) {
    return;
  }

  try {
    loadingPaymentMethod.value = true;

    if (poll) {
      for (let attempt = 0; attempt < PAYMENT_METHOD_POLLING_MAX_ATTEMPTS; attempt++) {
        // eslint-disable-next-line no-await-in-loop
        paymentMethod.value = await api.me.getPaymentMethod();
        if (paymentMethod.value) {
          return;
        }
        // eslint-disable-next-line no-await-in-loop
        await sleep(PAYMENT_METHOD_POLLING_INTERVAL_MS);
      }
    } else {
      paymentMethod.value = await api.me.getPaymentMethod();
    }
  } catch (error) {
    logger.error('Error loading payment method:', error)();
    paymentMethod.value = null;
  } finally {
    loadingPaymentMethod.value = false;
  }
};

const loadLicenseContracts = async(): Promise<void> => {
  try {
    loadingContracts.value = true;
    licenseContracts.value = await api.me.getContractsHistory();
  } catch (error) {
    logger.error('Error loading license contracts:', error)();
    licenseContracts.value = [];
  } finally {
    loadingContracts.value = false;
    // Load payment method after contracts - ensures currentContract is available for shouldShowPaymentMethod check
    void loadPaymentMethod();
  }
};

const closeDialogAndRefresh = async(message: string): Promise<void> => {
  showPaymentDialog.value = false;
  await refreshUserData();
  await loadLicenseContracts();
  $q.notify({type: 'positive', message});
};

const handlePaymentSuccess = async(): Promise<void> => {
  await refreshUserData();
  await loadLicenseContracts();
  await loadPaymentMethod(true);
};

const handleSubscriptionSynced = async(): Promise<void> => closeDialogAndRefresh($t('subscription_synced'));

const executeSubscriptionAction = async(
  action: () => Promise<void>,
  successMessage: string,
  errorMessage: string,
): Promise<void> => {
  subscriptionActionInProgress.value = true;
  try {
    await action();
    await refreshUserData();
    $q.notify({type: 'positive', message: successMessage});
  } catch (err) {
    logger.error('Subscription action error:', err)();
    $q.notify({type: 'negative', message: errorMessage});
  } finally {
    subscriptionActionInProgress.value = false;
  }
};

const handleReactivateSubscription = async(): Promise<void> =>
  executeSubscriptionAction(
    async() => api.me.reactivateSubscription(),
    $t('subscription_reactivated'),
    $t('subscription_reactivate_error'),
  );

const handleCancelSubscription = async(): Promise<void> =>
  executeSubscriptionAction(
    async() => api.me.cancelSubscription(),
    $t('subscription_canceled'),
    $t('subscription_cancel_error'),
  );

const shouldShowPurchasableLicenses = computed(() => {
  return userData.value?.isEligibleForPurchase ?? false;
});

const handleUpdateCardSuccess = async(): Promise<void> => {
  showUpdateCardDialog.value = false;
  await loadPaymentMethod();
  $q.notify({type: 'positive', message: $t('card_updated')});
};

const handleRenewVoucherSuccess = async(): Promise<void> => {
  showRenewVoucherDialog.value = false;
  await refreshUserData();
  await loadLicenseContracts();

  try {
    const homeContent = await api.me.getHomeContent();
    userStore.setHomeContent(homeContent);
  } catch (err) {
    logger.warn('Failed to refresh home content after voucher renewal:', err)();
  }

  $q.notify({type: 'positive', message: $t('voucher_renewed')});
};

const formatCardNumber = (last4: string): string => {
  return `**** **** **** ${last4}`;
};

const formatExpiry = (month: number, year: number): string => {
  const monthStr = String(month).padStart(2, '0');
  const yearStr = String(year).slice(-2);
  return `${monthStr}/${yearStr}`;
};

const loadPurchasableLicenses = async(): Promise<void> => {
  if (!shouldShowPurchasableLicenses.value) {
    return;
  }
  
  try {
    loadingLicenses.value = true;
    const licenses = await api.me.getPurchasableLicenses();
    purchasableLicenses.value = licenses
      .slice()
      .sort((a, b) => (a.productInfo?.price ?? 0) - (b.productInfo?.price ?? 0));
  } catch (_error) {
    purchasableLicenses.value = [];
  } finally {
    loadingLicenses.value = false;
  }
};

const formatPrice = (price: number | undefined, currency: string | undefined): string => {
  if (!price || !currency) {
    return $t('price_not_available');
  }

  return formatCurrency(price, currency, datePatterns.value.numberLocale);
};

const formatDate = (dateString: string): string => {
  // Intl over a dayjs token format: no locale bundle to load, and month names follow the profile
  // language like formatPrice already does for currency. The locale now carries the profile's
  // country as well, which is what orders the parts — an English reader in Germany was shown the
  // American order (IPD-3229).
  return new Intl.DateTimeFormat(datePatterns.value.intlLocale, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).format(new Date(dateString));
};

// Interval-aware plan labels/icon (shared mapping in planDisplay). Fall back to the product's
// own name/description (never a hardcoded "Monthly") when the interval is missing or unknown.
const planTitle = (product: ProductInfo | undefined): string => {
  const {titleKey} = planDisplay(product?.recurring);
  return titleKey ? $t(titleKey) : (product?.name ?? '');
};

const planDescription = (product: ProductInfo | undefined): string => {
  const {descriptionKey} = planDisplay(product?.recurring);
  return descriptionKey ? $t(descriptionKey) : (product?.description ?? '');
};

const planPeriod = (product: ProductInfo | undefined): string => {
  const {periodKey} = planDisplay(product?.recurring);
  return periodKey ? $t(periodKey) : '';
};

const planIcon = (product: ProductInfo | undefined): string => planDisplay(product?.recurring).icon;

const getContractCost = (contract: Contract): string => {
  if (contract.paymentSubscription?.product) {
    return formatPrice(contract.paymentSubscription.product.price, contract.paymentSubscription.product.currency);
  }
  return '-';
};

const getContractEndDate = (contract: Contract): string => {
  if (contract.paymentSubscription?.currentPeriodEnd) {
    return formatDate(contract.paymentSubscription.currentPeriodEnd);
  }
  // For voucher/gift contracts, calculate end date from createDate + validityPeriodInDays
  if (contract.license.validityPeriodInDays) {
    return formatDate(dayjs(contract.createDate).add(contract.license.validityPeriodInDays, 'days').toISOString());
  }
  return $t('expiry_never');
};

const {lockNoticePending, consumeLockNotice} = useLockNotice();

// Tracks whether the locked dialog is currently shown so bounce-backs from the
// expiry guard don't stack duplicate dialogs.
const isLockedDialogOpen = ref(false);

// Overridable copy keys for ContentLockedDialog — typed (not Record<string,string>)
// so a mistyped key name fails to compile.
type ContentLockedDialogCopy = Partial<{subtitleKey: string; descriptionKey: string; ctaKey: string}>;

// Provision-aware copy: an expired voucher is renewed with a code, while a
// direct-payment / gifted / no-contract user picks a plan (the default copy).
const lockedDialogProps = computed((): ContentLockedDialogCopy =>
  isVoucherLicense.value
    ? {
      subtitleKey: 'content_locked_voucher_subtitle',
      descriptionKey: 'content_locked_voucher_description',
      ctaKey: 'renew_voucher',
    }
    : {});

function showContentLockedDialog(): void {
  if (isLockedDialogOpen.value) {
    return;
  }
  isLockedDialogOpen.value = true;
  // Snapshot of the copy at open time is intentional: the dialog is only opened
  // once the contract is in the store (the guard requires it), so the provision
  // type is already settled and never changes for the dialog's lifetime.
  $q.dialog({component: ContentLockedDialog, componentProps: lockedDialogProps.value}).onDismiss(() => {
    isLockedDialogOpen.value = false;
  });
}

// The expiry guard sets a pending lock notice each time it blocks a locked
// navigation (including the first redirect here). Show the dialog and consume
// the flag.
function handleLockNotice(): void {
  if (lockNoticePending.value) {
    showContentLockedDialog();
    consumeLockNotice();
  }
}

// API-error-driven redirects from elsewhere (loading-error, explore, enrollment)
// land here with ?reason=no_active_licenses to flag a no-active-license user.
// Gate on the actual absence of an active contract so a stale/bookmarked URL
// can't pop the dialog for an active user, then clear the param.
function handleReasonQuery(): void {
  if (route.query.reason === ErrorCodesDto.NO_ACTIVE_LICENSES && !userData.value?.activeContract) {
    showContentLockedDialog();
    void router.replace({query: {}});
  }
}

// Re-fire on every later bounce-back / query change while the page stays mounted.
watch(lockNoticePending, handleLockNotice);
watch(() => route.query.reason, handleReasonQuery);

onMounted(() => {
  // Handle a notice/param raised before this page mounted (the hard-load redirect
  // lands here already pending). Done in onMounted — not an {immediate} watcher —
  // so the contract-derived dialog copy is read after reactive state has settled.
  handleLockNotice();
  handleReasonQuery();
  void loadPurchasableLicenses();
  void loadLicenseContracts();
});

</script>

<template>
  <!-- Current plan section -->
  <div
    class="current-plan-container"
    :class="{expired: hasExpired && !loadingContracts, 'direct-payment': isDirectPaymentLicense && !hasExpired}"
  >
    <div v-if="loadingContracts" class="current-plan-content" aria-hidden="true">
      <q-skeleton type="QChip" width="100px" height="28px" class="current-plan-chip-skeleton"/>
      <div class="current-plan-grid skeleton-decoration">
        <div class="current-plan-info">
          <q-skeleton type="text" width="200px" height="36px"/>
          <q-skeleton type="text" width="120px"/>
        </div>
      </div>
    </div>
    <div v-else class="current-plan-content">
      <badge-pill class="current-plan-chip" theme="white" :label="$t('current_plan')"/>
      <!-- Sun decoration and action button for direct payment -->
      <div v-if="isDirectPaymentLicense && !hasExpired" class="direct-payment-actions">
        <svg-illustration :name="planIcon(paymentSubscription?.product)" class="direct-payment-decoration"/>
        <balm-btn
          v-if="isSubscriptionCanceled"
          :label="$t('reactivate_subscription')"
          :loading="subscriptionActionInProgress"
          theme="white"
          paddings="p3-7"
          class="direct-payment-btn"
          @click="handleReactivateSubscription"
        />
        <balm-btn
          v-else
          :label="$t('cancel_plan')"
          :loading="subscriptionActionInProgress"
          theme="white"
          paddings="p3-7"
          class="direct-payment-btn"
          @click="handleCancelSubscription"
        />
      </div>
      <div class="current-plan-grid">
        <div v-if="!isDirectPaymentLicense || hasExpired" class="current-plan-decoration">
          <svg-illustration v-if="hasExpired" name="expired-plan-decoration.svg"/>
          <svg-illustration v-else name="high-five.svg"/>
        </div>
        <div class="current-plan-info">
          <!-- Expired state -->
          <template v-if="hasExpired && hasContract && license">
            <span class="license-name">
              <template v-if="isGiftedLicense">{{ isLongTermGifted ? $t('gifted_access_expired') : $t('trial_expired') }}</template>
              <template v-else-if="isVoucherLicense">{{ $t('voucher_expired') }}</template>
              <template v-else>{{ $t('license_expired') }}</template>
            </span>
            <div v-if="isVoucherLicense" class="expired-license-info">
              <balm-btn
                :label="$t('renew_voucher')"
                theme="grey"
                paddings="p3-7"
                class="renew-voucher-btn"
                @click="showRenewVoucherDialog = true"
              />
            </div>
            <div v-else class="expired-license-info">
              <span class="choose-plan-message">{{ $t('choose_plan_message') }}</span>
            </div>
          </template>

          <!-- Active state -->
          <template v-else>
            <span v-if="hasContract && license" class="license-name">
              <template v-if="isVoucherLicense">
                {{ $t('voucher_access') }}
              </template>
              <template v-else-if="isLongTermGifted">
                {{ $t('gifted_access') }}
              </template>
              <template v-else-if="isGiftedLicense && !isLifetimeValidity">
                {{ $t('trial_access') }}
              </template>
              <template v-else-if="isDirectPaymentLicense">
                {{ planTitle(paymentSubscription?.product) || license.name }}
              </template>
              <template v-else>
                {{ license.name }}
              </template>
            </span>
            <span v-else class="license-name">{{ $t('subscription_unavailable') }}</span>

            <!-- Direct Payment (Stripe subscription) details -->
            <template v-if="hasContract && license && isDirectPaymentLicense">
              <div class="subscription-renewal-info">
                <template v-if="!isSubscriptionCanceled">
                  {{ $t('subscription_renews_label') }}
                  <formatted-date v-if="expireDate" :date="expireDate" :format="'date'">
                    ({{ $t('subscription_expire_remaining_days', {n: daysLeftToExpireDate}) }})
                  </formatted-date>
                </template>
                <template v-else>
                  {{ $t('subscription_valid_until_label') }}
                  <formatted-date v-if="expireDate" :date="expireDate" :format="'date'">
                    ({{ $t('subscription_expire_remaining_days', {n: daysLeftToExpireDate}) }})
                  </formatted-date>
                </template>
              </div>
            </template>

            <!-- Voucher license details -->
            <template v-else-if="hasContract && license && isVoucherLicense">
              <div class="voucher-license-info">
                <span v-if="!isLifetimeValidity && expireDate">
                  {{ $t('expires_on') }}
                  <formatted-date :date="expireDate" :format="'date'"/>
                  ({{ $t('subscription_expire_remaining_days', {n: daysLeftToExpireDate}) }})
                </span>
                <span v-else-if="isLifetimeValidity">
                  {{ $t('subscription_duration_lifetime') }}
                </span>
              </div>
            </template>

            <!-- Gifted license details (e.g., Trial Access, Gifted Access) -->
            <template v-else-if="hasContract && license && isGiftedLicense">
              <div class="gifted-license-info">
                <span v-if="!isLifetimeValidity && expireDate">
                  {{ $t('expires_on') }}
                  <formatted-date :date="expireDate" :format="'date'"/>
                  ({{ $t('subscription_expire_remaining_days', {n: daysLeftToExpireDate}) }})
                  <span v-if="!isLongTermGifted" class="choose-plan-message">{{ $t('choose_plan_message') }}</span>
                </span>
                <span v-else-if="isLifetimeValidity">
                  {{ $t('subscription_duration_lifetime') }}
                </span>
              </div>
            </template>
          </template>
        </div>
      </div>
    </div>
  </div>

  <!-- Payment Method section -->
  <div v-if="shouldShowPaymentMethod" class="account-tab-page">
    <h3 class="info-title">
      {{ $t('payment_method') }}
    </h3>
    <div class="payment-method-container">
      <div v-if="loadingPaymentMethod" class="payment-method-content" aria-hidden="true">
        <div class="payment-method-details">
          <div class="payment-detail payment-detail-number">
            <q-skeleton type="text" width="50px" height="14px"/>
            <q-skeleton type="text" width="180px" height="20px"/>
          </div>
          <div class="payment-detail payment-detail-expires">
            <q-skeleton type="text" width="50px" height="14px"/>
            <q-skeleton type="text" width="60px" height="20px"/>
          </div>
        </div>
        <q-skeleton type="QBtn" width="130px" height="44px" class="skeleton-btn"/>
      </div>
      <div v-else-if="paymentMethod" class="payment-method-content">
        <div class="payment-method-details">
          <div class="payment-detail payment-detail-number">
            <span class="payment-label">{{ $t('number') }}</span>
            <span class="payment-value">{{ formatCardNumber(paymentMethod.last4) }}</span>
          </div>
          <div class="payment-detail payment-detail-expires">
            <span class="payment-label">{{ $t('expires') }}</span>
            <span class="payment-value">{{ formatExpiry(paymentMethod.expMonth, paymentMethod.expYear) }}</span>
          </div>
        </div>
        <balm-btn
          :label="$t('update_card')"
          theme="grey"
          paddings="p3-7"
          class="update-card-btn"
          @click="showUpdateCardDialog = true"
        />
      </div>
      <div v-else class="no-payment-method">
        {{ $t('no_payment_method') }}
      </div>
    </div>
  </div>

  <div v-if="shouldShowPurchasableLicenses && purchasableLicenses.length > 0" class="account-tab-page">
    <h3 class="info-title">
      {{ $t('other_plans') }}
    </h3>
    <!-- Purchasable licenses section -->
    <div class="purchasable-licenses-section">
      <div class="licenses-grid">
        <div
          v-for="(purchaseLicense, index) in purchasableLicenses"
          :key="purchaseLicense.license?.id || `license-${index}`"
          class="license-card"
        >
          <div class="license-card-content">
            <svg-illustration :name="planIcon(purchaseLicense.productInfo)" class="license-icon"/>
            <h5 class="license-name">
              {{ planTitle(purchaseLicense.productInfo) || purchaseLicense.license.name }}
            </h5>
            <span class="license-description">{{ planDescription(purchaseLicense.productInfo) }}</span>
            <div class="license-price-container">
              <span class="license-price">
                {{ formatPrice(purchaseLicense.productInfo?.price, purchaseLicense.productInfo?.currency) }}
              </span>
              <span v-if="planPeriod(purchaseLicense.productInfo)" class="license-period">
                {{ planPeriod(purchaseLicense.productInfo) }}
              </span>
            </div>
            <balm-btn
              :label="$t('select_plan')"
              theme="grey"
              paddings="p3-7"
              class="select-plan-btn"
              @click="openPaymentDialog(purchaseLicense.license.id)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Payment history section -->
  <div class="account-tab-page">
    <h3 class="info-title">
      {{ $t('payment_history') }}
    </h3>
    
    <div class="payment-history-container">
      <div v-if="loadingContracts" aria-hidden="true">
        <!-- Mobile skeleton cards -->
        <div class="contracts-cards">
          <div v-for="n in 2" :key="n" class="contract-card">
            <div class="contract-card-header">
              <q-skeleton type="text" width="120px"/>
              <q-skeleton type="text" width="60px"/>
            </div>
            <div class="contract-card-dates">
              <div class="date-item">
                <q-skeleton type="text" width="40px" height="12px"/>
                <q-skeleton type="text" width="90px"/>
              </div>
              <div class="date-item">
                <q-skeleton type="text" width="30px" height="12px"/>
                <q-skeleton type="text" width="90px"/>
              </div>
            </div>
          </div>
        </div>
        <!-- Desktop skeleton table -->
        <div class="contracts-table-container">
          <table class="contracts-table">
            <thead>
              <tr>
                <th class="plan-column">
                  {{ $t('plan') }}
                </th>
                <th class="cost-column">
                  {{ $t('cost') }}
                </th>
                <th class="start-column">
                  {{ $t('start') }}
                </th>
                <th class="end-column">
                  {{ $t('end') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="n in 2" :key="n" class="contract-row">
                <td class="plan-cell">
                  <q-skeleton type="text" width="120px"/>
                </td>
                <td class="cost-cell">
                  <q-skeleton type="text" width="60px"/>
                </td>
                <td class="start-cell">
                  <q-skeleton type="text" width="90px"/>
                </td>
                <td class="end-cell">
                  <q-skeleton type="text" width="60px"/>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-else-if="licenseContracts.length > 0" class="contracts-list">
        <!-- Mobile card view -->
        <div class="contracts-cards">
          <div
            v-for="(contract, index) in licenseContracts"
            :key="`card-${contract.id || index}`"
            class="contract-card"
          >
            <div class="contract-card-header">
              <span class="contract-plan-name">{{ contract.license.name }}</span>
              <span class="contract-cost">{{ getContractCost(contract) }}</span>
            </div>
            <div class="contract-card-dates">
              <div class="date-item">
                <span class="date-label">{{ $t('start') }}</span>
                <span class="date-value">{{ formatDate(contract.createDate) }}</span>
              </div>
              <div class="date-item">
                <span class="date-label">{{ $t('end') }}</span>
                <span class="date-value">{{ getContractEndDate(contract) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Desktop table view -->
        <div class="contracts-table-container">
          <table class="contracts-table">
            <thead>
              <tr>
                <th class="plan-column">
                  {{ $t('plan') }}
                </th>
                <th class="cost-column">
                  {{ $t('cost') }}
                </th>
                <th class="start-column">
                  {{ $t('start') }}
                </th>
                <th class="end-column">
                  {{ $t('end') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(contract, index) in licenseContracts"
                :key="contract.id || `contract-${index}`"
                class="contract-row"
              >
                <td class="plan-cell">
                  {{ contract.license.name }}
                </td>
                <td class="cost-cell">
                  {{ getContractCost(contract) }}
                </td>
                <td class="start-cell">
                  {{ formatDate(contract.createDate) }}
                </td>
                <td class="end-cell">
                  {{ getContractEndDate(contract) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <div v-else class="no-contracts-available">
        {{ $t('no_payment_history') }}
      </div>
    </div>
  </div>

  <!-- Payment Dialog -->
  <payment-dialog
    v-if="selectedLicenseId"
    v-model="showPaymentDialog"
    :license-id="selectedLicenseId"
    @payment-success="handlePaymentSuccess"
    @payment-pending="handlePaymentPending"
    @payment-error="handlePaymentError"
    @subscription-synced="handleSubscriptionSynced"
  />

  <!-- Update Card Dialog -->
  <update-card-dialog
    v-model="showUpdateCardDialog"
    @update-success="handleUpdateCardSuccess"
  />

  <!-- Renew Voucher Dialog -->
  <renew-voucher-dialog
    v-model="showRenewVoucherDialog"
    @renew-success="handleRenewVoucherSuccess"
  />
</template>

<style lang="scss" scoped>

.account-tab-page {
  @include q-card-white;
  padding: var(--Spacing-spacing-5);

  .info-title {
    @include font-display-3;
    color: var(--Neutral-UI-neutral-ui-100);
    margin-bottom: var(--Spacing-spacing-5);
  }

  &:not(:first-child) {
    margin-top: var(--Spacing-spacing-5);
  }
}

.current-plan-container {
  position: relative;
  border-radius: var(--Border-Radius-1);
  background: var(--Light-Scheme-Secondary-secondary-color-1);
  padding: var(--Spacing-spacing-6);
  overflow: hidden;

  @include screen-md {
    padding-bottom: 0;
  }

  &.expired {
    background: var(--Light-Scheme-Secondary-secondary-color-3);
    padding-bottom: var(--Spacing-spacing-6);
  }

  &.direct-payment {
    @include screen-md {
      padding-bottom: var(--Spacing-spacing-6);
    }

    @include screen-lg {
      min-height: 280px;
      padding-bottom: 0;
    }
  }
}

.direct-payment-actions {
  display: contents;

  @include screen-lg {
    display: flex;
    position: absolute;
    top: 50%;
    right: var(--Spacing-spacing-6);
    transform: translateY(-50%);
    flex-direction: row;
    align-items: center;
    gap: var(--Spacing-spacing-4);
  }

  .direct-payment-decoration {
    width: 100px;
    height: 100px;

    @include screen-lg {
      width: 250px;
      height: 250px;
    }

    :deep(svg) {
      width: 100%;
      height: 100%;
    }
  }

  .direct-payment-btn {
    order: 1;
    background-color: var(--Light-Scheme-Neutral-UI-neutral-ui-10) !important;
    color: var(--Light-Scheme-Neutral-UI-neutral-ui-100) !important;

    @include screen-md {
      order: unset;
      position: absolute;
      top: 50%;
      right: var(--Spacing-spacing-6);
      transform: translateY(-50%);
    }

    @include screen-lg {
      position: static;
      transform: none;
    }
  }
}


.direct-payment-btn {
  width: 100%;

  @include screen-md {
    width: auto;
  }
}


.current-plan-content {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-4);
}

.current-plan-chip {
  align-self: flex-start;
  // Force white theme regardless of color mode
  background-color: var(--Light-Scheme-Neutral-UI-neutral-ui-10) !important;
  color: var(--Light-Scheme-Neutral-UI-neutral-ui-100) !important;
}

.current-plan-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--Spacing-spacing-4);
  grid-template-areas:
    'decoration'
    'info';

  @include screen-md {
    grid-template-columns: 1fr auto;
    grid-template-areas: 'info decoration';
    align-items: center;
  }
}

.current-plan-decoration {
  grid-area: decoration;
  display: flex;
  justify-content: center;
  align-items: center;

  :deep(svg),
  :deep(img) {
    max-height: 150px;
    width: auto;
  }

  @include screen-md {
    justify-content: flex-end;
    align-items: flex-end;
    margin-right: var(--Spacing-spacing-6);

    :deep(svg),
    :deep(img) {
      max-height: none;
    }
  }
}

.current-plan-info {
  grid-area: info;
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-4);

  .license-name {
    @include font-display-3;
    color: var(--Light-Scheme-Neutral-UI-neutral-ui-100);
  }

  .subscription-renewal-info,
  .gifted-license-info,
  .voucher-license-info {
    @include font-body;
    color: var(--Light-Scheme-Neutral-UI-neutral-ui-70);

    .expired-text {
      color: var(--Primary-primary-color-3);
    }

    .choose-plan-message {
      display: block;
      margin-top: var(--Spacing-spacing-2);
    }
  }

  .expired-license-info {
    @include font-body;
    color: var(--Light-Scheme-Neutral-UI-neutral-ui-70);

    .renew-voucher-btn {
      margin-top: var(--Spacing-spacing-2);
      background-color: var(--Light-Scheme-Neutral-UI-neutral-ui-20);
      color: var(--Light-Scheme-Neutral-UI-neutral-ui-100);
    }
  }
}

// Payment Method section styles
.payment-method-container {
  margin-top: var(--Spacing-spacing-5);
}

.payment-method-content {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-5);
}

.payment-method-details {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-4);

  @include screen-md {
    flex-direction: row;
    gap: var(--Spacing-spacing-8);
  }
}

.payment-detail {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-1);

  .payment-label {
    @include font-body-sm;
    color: var(--Neutral-UI-neutral-ui-60);
  }

  .payment-value {
    @include font-body-bold;
    color: var(--Neutral-UI-neutral-ui-100);
    border-bottom: 1px solid var(--Neutral-UI-neutral-ui-30);
    padding-bottom: var(--Spacing-spacing-2);
  }
}

.payment-detail-number {
  @include screen-md {
    flex: 2;
    max-width: 400px;
  }
}

.payment-detail-expires {
  @include screen-md {
    flex: 1;
    max-width: 150px;
  }
}

.update-card-btn {
  width: 100%;

  @include screen-md {
    width: auto;
    align-self: flex-start;
  }
}

.no-payment-method {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-60);
}

.purchasable-licenses-section {
  margin-top: var(--Spacing-spacing-5);
}

.current-plan-chip-skeleton {
  border-radius: 20px;
}

.skeleton-decoration {
  border-radius: var(--Border-Radius-1);
  height:280px
}

.skeleton-btn {
  border-radius: var(--button-border-radius);
}

.no-licenses-available {
  @include q-card-10;
  @include font-display-4;
  padding: var(--Spacing-spacing-8);
  color: var(--Neutral-UI-neutral-ui-100);
  text-align: center;
}

.licenses-grid {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-5);

  @include screen-lg {
    flex-direction: row;
    justify-content: flex-start;
  }
}

.license-card {
  @include q-card-10;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: var(--Spacing-spacing-7);

  @include screen-lg {
    max-width: 480px;
  }
}

.license-card-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--Spacing-spacing-4);
  width: 100%;
  flex: 1;

  .license-name {
    @include font-display-4;
    color: var(--Neutral-UI-neutral-ui-100);
  }

  .license-description {
    @include font-body;
    color: var(--Neutral-UI-neutral-ui-60);
  }

  .license-price-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    // Pin price + CTA to the card bottom so sibling cards align regardless of description length
    margin-top: auto;
  }

  .license-price {
    @include font-metric-lg;
    color: var(--Neutral-UI-neutral-ui-100);
  }

  .license-period {
    @include font-body;
    color: var(--Neutral-UI-neutral-ui-60);
  }

  .select-plan-btn {
    width: 100%;
  }
}

.payment-history-container {
  margin-top: var(--Spacing-spacing-5);
}

// Mobile card view
.contracts-cards {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-4);

  @include screen-md {
    display: none;
  }
}

.contract-card {
  @include q-card-10;
  padding: var(--Spacing-spacing-5);
  border-radius: var(--Border-Radius-1);
}

.contract-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--Spacing-spacing-3);
  margin-bottom: var(--Spacing-spacing-4);
  padding-bottom: var(--Spacing-spacing-4);
  border-bottom: 1px solid var(--Neutral-UI-neutral-ui-20);
}

.contract-plan-name {
  @include font-body-bold;
  color: var(--Neutral-UI-neutral-ui-100);
  flex: 1;
}

.contract-cost {
  @include font-body-bold;
  color: var(--Neutral-UI-neutral-ui-100);
  white-space: nowrap;
}

.contract-card-dates {
  display: flex;
  gap: var(--Spacing-spacing-6);
}

.date-item {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-1);
}

.date-label {
  @include font-body-sm;
  color: var(--Neutral-UI-neutral-ui-60);
  text-transform: uppercase;
  font-weight: 600;
  font-size: 11px;
  letter-spacing: 0.5px;
}

.date-value {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-80);
}

// Desktop table view
.contracts-table-container {
  display: none;

  @include screen-md {
    display: block;
    @include q-card-white;
    border-radius: var(--Border-Radius-1);
    overflow: hidden;
  }
}

.contracts-table {
  width: 100%;
  border-collapse: collapse;
  
  thead {
    background: var(--Neutral-UI-neutral-ui-10);
    
    th {
      @include font-body-bold;
      color: var(--Neutral-UI-neutral-ui-100);
      padding: var(--Spacing-spacing-4);
      text-align: left;
      border-bottom: 1px solid var(--Neutral-UI-neutral-ui-30);
      
      &.plan-column {
        width: auto;
        min-width: 200px;
      }
      
      &.cost-column,
      &.start-column,
      &.end-column {
        width: 120px;
        text-align: center;
        white-space: nowrap;

        @include screen-md {
          width: 140px;
        }
      }
    }
  }
  
  tbody {
    .contract-row {
      border-bottom: 1px solid var(--Neutral-UI-neutral-ui-20);
          
      &:last-child {
        border-bottom: none;
      }
      
      td {
        @include font-body;
        color: var(--Neutral-UI-neutral-ui-80);
        padding: var(--Spacing-spacing-4);
        
        &.plan-cell {
          @include font-body-bold;
          color: var(--Neutral-UI-neutral-ui-100);
        }
        
        &.cost-cell,
        &.start-cell,
        &.end-cell {
          text-align: center;
          white-space: nowrap;
        }
        
        &.cost-cell {
          @include font-body-bold;
          color: var(--Neutral-UI-neutral-ui-100);
        }
      }
    }
  }
}

.no-contracts-available {
  @include q-card-10;
  @include font-display-4;
  padding: var(--Spacing-spacing-8);
  color: var(--Neutral-UI-neutral-ui-100);
  text-align: center;
}

// Responsive adjustments for the table
@include screen-sm {
  .contracts-table {
    thead th {
      padding: var(--Spacing-spacing-5);
    }
    
    tbody td {
      padding: var(--Spacing-spacing-5);
    }
  }
}

</style>
